import { injectable, symbolToken } from '@servicetitan/react-ioc';
import {
    AppSettings,
    Case,
    CaseAttachment,
    Client,
    FullStoryData,
    ITenantUser,
    RecentCaseSummary,
    SimilarJiraIssue,
    User,
} from './native-client.api';

export const MAIN_API_TOKEN = symbolToken<IMainApi>('MAIN_API_TOKEN');

export interface IMainApi {
    /**
     * Get application settings
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with application settings
     */
    getSettings(signal?: AbortSignal): Promise<AppSettings>;

    /**
     * Get case information
     * @param caseNumber SF case number
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with case information
     */
    getCase(caseNumber: string, signal?: AbortSignal): Promise<Case>;

    /**
     * Get case media (attachments and images)
     * @param caseNumber SF case number
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with array of case media
     */
    getCaseMedia(caseNumber: string, signal?: AbortSignal): Promise<CaseAttachment[]>;

    /**
     * Get full story data for a chatbot session
     * @param chatbotSessionId Chatbot session ID
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with full story data
     */
    getCaseFullStory(chatbotSessionId: string, signal?: AbortSignal): Promise<FullStoryData>;

    /**
     * Search cases by query
     * @param query Search query string
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with array of cases matching the search query
     */
    search(query: string, signal?: AbortSignal): Promise<Case[]>;

    /**
     * Get tenant user information
     * @param tenantId Tenant ID
     * @param userId User ID
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with tenant user information
     */
    tenantUser(tenantId: number, userId: number, signal?: AbortSignal): Promise<ITenantUser>;

    /**
     * Get current user information
     */
    me(): Promise<User>;

    /**
     * Get similar Jira issues for a case
     * @param caseNumber SF case number
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with array of similar Jira issues
     */
    getSimilarJiraIssues(caseNumber: string, signal?: AbortSignal): Promise<SimilarJiraIssue[]>;

    /**
     * Get recent case summaries for a case
     * @param caseNumber SF case number
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with array of recent case summaries
     */
    getRecentCaseSummary(caseNumber: string, signal?: AbortSignal): Promise<RecentCaseSummary[]>;

    /**
     * Get case summary for from transcript
     * @param caseNumber SF case number
     * @param signal Optional abort signal for request cancellation
     * @returns Promise with case summary
     */
    getCaseSummary(sessionId: string, signal?: AbortSignal): Promise<string>;
}

@injectable()
export class MainApi implements IMainApi {
    private readonly apiVersion = '1';
    private readonly client: Client;

    constructor() {
        this.client = new Client('');
    }

    async getSettings(signal?: AbortSignal) {
        return this.client.settings(this.apiVersion, signal);
    }

    async search(query: string, signal?: AbortSignal) {
        return this.client.search(query, this.apiVersion, signal);
    }

    async getCase(caseNumber: string, signal?: AbortSignal) {
        return this.client.cases(caseNumber, this.apiVersion, signal);
    }

    async getCaseMedia(caseNumber: string, signal?: AbortSignal) {
        return this.client.attachments(caseNumber, this.apiVersion, signal);
    }

    async getCaseFullStory(chatbotSessionId: string, signal?: AbortSignal) {
        return this.client.fullstoryData(chatbotSessionId, this.apiVersion, signal);
    }

    async tenantUser(tenantId: number, userId: number, signal?: AbortSignal) {
        return this.client.user(tenantId, userId, this.apiVersion, signal);
    }

    async me() {
        return this.client.me(this.apiVersion);
    }

    async getRecentCaseSummary(caseNumber: string, signal?: AbortSignal) {
        return this.client.recentCaseSummary(caseNumber, this.apiVersion, signal);
    }

    async getSimilarJiraIssues(caseNumber: string, signal?: AbortSignal) {
        return this.client.similarJiraIssues(caseNumber, this.apiVersion, signal);
    }

    async getCaseSummary(sessionId: string, signal?: AbortSignal) {
        return this.client.caseSummary(sessionId, this.apiVersion, signal);
    }
}
